// Visual Configuration - Centralized styling constants
export const visualConfig = {
  // Color Schemes for Each Tab
  colors: {
    carton: {
      gradient: 'from-blue-500 to-indigo-500',
      button: 'bg-blue-500 text-white hover:bg-blue-600',
      ring: 'focus:ring-blue-200'
    },
    pallet: {
      gradient: 'from-green-500 to-emerald-500', 
      button: 'bg-green-500 text-white hover:bg-green-600',
      ring: 'focus:ring-green-200',
      checkbox: 'text-green-500'
    },
    container: {
      gradient: 'from-orange-500 to-red-500',
      button: 'bg-orange-500 text-white hover:bg-orange-600', 
      ring: 'focus:ring-orange-200'
    },
    settings: {
      gradient: 'from-purple-500 to-pink-500',
      button: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600',
      ring: 'focus:ring-purple-200',
      checkbox: 'text-purple-500'
    },
    results: {
      gradient: 'from-emerald-500 to-teal-500'
    },
    
    // Navigation & General
    navigation: {
      active: 'data-[state=active]:bg-blue-500 data-[state=active]:text-white',
      inactive: 'text-xs sm:text-sm md:text-base font-medium rounded-xl',
      background: 'bg-white/80 backdrop-blur-sm'
    },
    
    // Neutral Colors
    neutral: {
      previous: 'bg-gray-200 text-gray-700 hover:bg-gray-300 focus:ring-gray-200',
      background: 'bg-white/90 backdrop-blur-sm',
      content: 'bg-gray-50/70',
      text: {
        primary: 'text-gray-900',
        secondary: 'text-gray-600', 
        muted: 'text-gray-500',
        disabled: 'text-gray-400'
      }
    }
  },

  // Layout & Sizing - Viewport Height Optimized
  layout: {
    // Card dimensions - Scale down on mobile for better height fit
    card: {
      minHeight: 'min-h-[70vh] sm:min-h-[75vh] md:min-h-[80vh] lg:min-h-[600px]',
      minHeightSettings: 'min-h-[60vh] sm:min-h-[65vh] md:min-h-[70vh] lg:min-h-[500px]',
      background: 'bg-white/90 backdrop-blur-sm',
      shape: 'rounded-2xl sm:rounded-3xl shadow-xl border-0'
    },
    
    // Header styling - Ultra compact for maximum height efficiency
    header: {
      padding: 'px-3 py-1 sm:px-4 sm:py-2 md:px-6 md:py-3 lg:px-8 lg:py-4',
      title: 'text-sm sm:text-base md:text-lg lg:text-xl font-semibold tracking-tight',
      description: 'text-xs sm:text-sm md:text-base'
    },
    
    // Content areas - Tight spacing on mobile, generous on desktop
    content: {
      padding: 'p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8',
      spacing: 'space-y-2 sm:space-y-3 md:space-y-4 lg:space-y-5 xl:space-y-6'
    },
    
    // Footer/Navigation - Compact on mobile
    footer: {
      padding: 'p-3 sm:p-4 md:p-6 lg:p-8 bg-gray-50/30 mt-auto',
      layout: 'flex justify-between items-center'
    },
    
    // Container & spacing - Viewport height aware
    container: {
      maxWidth: 'max-w-5xl',
      padding: 'p-2 sm:p-3 md:p-4 lg:p-6 pt-3 sm:pt-4 md:pt-5 lg:pt-6 xl:pt-8',
      background: 'bg-transparent',
      height: 'min-h-screen'
    }
  },

  // Typography - Ultra Compact Mobile Start, Responsive Scaling
  typography: {
    // Headers - Start smaller, scale up generously
    mainTitle: 'text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-semibold text-gray-900 tracking-tight leading-tight',
    subtitle: 'text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 font-medium mt-1',
    
    // Section headings - Very compact mobile start
    sectionTitle: 'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-semibold text-gray-900',
    subsectionTitle: 'text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-semibold',
    
    // Labels and inputs - Ultra compact mobile, readable desktop
    label: 'text-xs sm:text-sm md:text-base lg:text-lg font-medium',
    labelDisabled: 'text-xs sm:text-sm md:text-base lg:text-lg font-medium text-gray-400',
    
    // Body text - Extra compact mobile scaling
    body: 'text-xs sm:text-sm md:text-sm lg:text-sm',
    small: 'text-xs md:text-sm lg:text-base',
    tiny: 'text-xs'
  },

  // Interactive Elements - Ultra Compact Mobile Scaling
  interactive: {
    // Buttons - Start very small, scale up
    button: {
      base: 'px-3 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 lg:px-8 lg:py-4 text-xs sm:text-sm md:text-base lg:text-lg rounded-lg sm:rounded-xl md:rounded-2xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200',
      focus: 'focus:outline-none focus:ring-4'
    },
    
    // Form elements - Compact on mobile for height efficiency
    form: {
      background: 'bg-gray-50/70 rounded-xl sm:rounded-2xl border border-gray-200/50',
      padding: 'p-3 sm:p-4 md:p-5 lg:p-6',
      spacing: 'space-y-2 sm:space-y-3'
    },
    
    // Checkboxes - Scale with screen size
    checkbox: {
      size: 'w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7',
      shape: 'border-2 border-gray-300 rounded-lg',
      focus: 'focus:ring-2'
    },
    
    // Select dropdowns - Responsive rounding
    select: {
      shape: 'rounded-xl sm:rounded-2xl',
      disabled: 'opacity-50 cursor-not-allowed'
    }
  },

  // Special Components - Height Optimized
  components: {
    // Tabs navigation - Ultra compact for maximum height efficiency
    tabs: {
      container: 'grid w-full grid-cols-5 h-8 sm:h-10 md:h-12 rounded-lg sm:rounded-xl p-0.5 sm:p-1 shadow-sm border border-gray-200/50',
      trigger: 'rounded-md sm:rounded-lg transition-all duration-200'
    },
    
    // Logo - Compact for maximum height efficiency
    logo: {
      size: 'h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24',
      style: 'object-contain flex-shrink-0'
    },
    
    // Build info - Ultra compact for height efficiency
    buildInfo: 'text-center mt-1 sm:mt-2 text-xs text-gray-500',
    
    // Results display
    results: {
      summary: 'bg-emerald-50/70 rounded-2xl',
      metric: 'text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-emerald-600',
      breakdown: 'bg-gray-50/70 rounded-2xl',
      visualization: 'aspect-square max-w-sm mx-auto'
    },
    
    // Warnings & alerts
    alerts: {
      error: 'bg-red-50 border border-red-200 rounded-2xl',
      warning: 'bg-red-50 border border-red-200 rounded-xl', 
      info: 'bg-blue-50 border border-blue-200 rounded-xl'
    }
  },

  // Animation & Transitions
  animations: {
    transition: 'transition-all duration-200',
    hover: 'hover:shadow-xl transform hover:scale-105'
  },

  // Viewport Height Utilities
  viewport: {
    // Use these for elements that should adapt to screen height
    fullHeight: 'min-h-screen',
    cardHeight: 'min-h-[70vh] sm:min-h-[75vh] md:min-h-[80vh] lg:min-h-[85vh]',
    compactHeight: 'min-h-[60vh] sm:min-h-[65vh] md:min-h-[70vh] lg:min-h-[75vh]',
    // Responsive margins for height optimization
    verticalSpacing: {
      tight: 'my-1 sm:my-2 md:my-3',
      normal: 'my-2 sm:my-3 md:my-4 lg:my-6',
      loose: 'my-3 sm:my-4 md:my-6 lg:my-8'
    }
  }
} as const;

// Helper function to get tab-specific colors
export function getTabColors(tab: 'carton' | 'pallet' | 'container' | 'settings' | 'results') {
  return visualConfig.colors[tab];
}

// Helper function to combine classes
export function combineClasses(...classes: (string | undefined | boolean)[]): string {
  return classes.filter(Boolean).join(' ');
} 